import { useEffect, useState } from "react";
import api, { fmtDate, fmtErr, fmtMoney, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { toast } from "sonner";
import { useAuth } from "../../AuthContext";

const CATEGORIES = { tree_limb: "Tree / limb", drainage: "Drainage", pest: "Pest", structural: "Structural", access_safety: "Access / safety", turf_health: "Turf health", other: "Other" };
const URGENCY_CLS = { routine: "bg-secondary text-secondary-foreground", soon: "bg-[hsl(43,74%,50%)] text-[hsl(240,10%,10%)]", urgent: "bg-destructive text-destructive-foreground" };
const STATUS_LABEL = { new: "New", triaged_quote: "Quoted", triaged_refer: "Referred", triaged_notify: "Owner notified", closed: "Closed" };

export default function Issues() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("new");
  const [triageFor, setTriageFor] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/observations").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const triage = async (oid, action) => {
    if (action === "close" && note.trim().length < 10) return toast.error("Closing needs a typed reason of at least 10 characters");
    try {
      await api.post(`/observations/${oid}/triage`, { action, note });
      toast.success({ quote: "Quoted — it's on the Add-ons page ready to price", refer: "Marked as referred out — owner emailed", notify: "Owner emailed", close: "Closed" }[action]);
      setTriageFor(null); setNote("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!items) return <Layout title="Issues"><p className="text-muted-foreground">Loading…</p></Layout>;
  const shown = items.filter((o) => tab === "new" ? o.status === "new" : tab === "triaged" ? o.status.startsWith("triaged") : o.status === "closed");

  return (
    <Layout title="Issues">
      <div className="mb-4 flex gap-2" data-testid="issues-tabs">
        {[["new", `New (${items.filter((o) => o.status === "new").length})`], ["triaged", "Triaged"], ["closed", "Closed"]].map(([k, label]) => (
          <button key={k} data-testid={`issues-tab-${k}`} onClick={() => setTab(k)}
            className={`h-10 flex-1 rounded-md border text-xs font-bold transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="issues-list">
        {shown.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">Nothing here. Crew can log issues from any job sheet with "Log an issue".</p>}
        {shown.map((o) => (
          <div key={o.id} className="card-flat space-y-2 p-4" data-testid={`issue-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold">{o.property_name}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(o.created_at?.slice(0, 10))} · by {o.logged_by_name} ({o.logged_by_role})</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${URGENCY_CLS[o.urgency] || "bg-muted"}`}>{o.urgency}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-bold">{CATEGORIES[o.category] || o.category}</span>
              </div>
            </div>
            <p className="text-sm" data-testid={`issue-desc-${o.id}`}>{o.description}</p>
            {o.photos?.length > 0 && <div className="flex flex-wrap gap-2">{o.photos.map((p, i) => <img key={`${i}-${p.length}`} src={photoSrc(p)} alt="" className="h-16 w-16 rounded-md object-cover" />)}</div>}
            <p className="text-xs font-bold text-muted-foreground">{STATUS_LABEL[o.status]}
              {o.owner_notified_at && <span className="text-primary" data-testid={`issue-notified-${o.id}`}> · owner notified {fmtDate(o.owner_notified_at.slice(0, 10))}</span>}
            </p>
            {o.triage_note && <p className="rounded-md bg-muted p-2 text-xs">{o.triage_note}</p>}
            {o.instruction_id && <p className="text-xs font-bold text-primary" data-testid={`issue-instr-${o.id}`}>Quote created — see Add-ons</p>}
            {o.status === "new" && user?.role === "operator" && (
              <>
                <button data-testid={`triage-btn-${o.id}`} onClick={() => { setTriageFor(triageFor === o.id ? null : o.id); setNote(""); }}
                  className="h-12 w-full rounded-md bg-secondary font-bold text-secondary-foreground">Triage</button>
                {triageFor === o.id && (
                  <div className="space-y-3 rounded-md border border-border p-3" data-testid={`triage-panel-${o.id}`}>
                    <p className="text-xs text-muted-foreground">The owner is emailed for quote, refer and notify — never for close. Notifying is the default for a reason: an untold hazard on file is a liability.</p>
                    <textarea data-testid={`triage-note-${o.id}`} className="field-input h-20 w-full py-2" placeholder="Note (required for close, min 10 chars)" value={note} onChange={(e) => setNote(e.target.value)} />
                    <button data-testid={`triage-notify-${o.id}`} onClick={() => triage(o.id, "notify")} className="h-12 w-full rounded-md bg-primary font-bold text-primary-foreground">Notify owner (default)</button>
                    <div className="grid grid-cols-3 gap-2">
                      <button data-testid={`triage-quote-${o.id}`} onClick={() => triage(o.id, "quote")} className="h-12 rounded-md bg-secondary text-xs font-bold text-secondary-foreground">Create quote</button>
                      <button data-testid={`triage-refer-${o.id}`} onClick={() => triage(o.id, "refer")} className="h-12 rounded-md bg-secondary text-xs font-bold text-secondary-foreground">Refer out</button>
                      <button data-testid={`triage-close-${o.id}`} onClick={() => triage(o.id, "close")} className="h-12 rounded-md border border-destructive text-xs font-bold text-destructive">Close</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
